import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { AlertTriangle, Home, ArrowRight } from "lucide-react";

export const NotFound = () => (
  <PublicLayout>
    <section className="py-24 min-h-[70vh] bg-[var(--ft-soft-gray)]">
      <div className="ft-container max-w-2xl text-center">
        <div className="text-[0.65rem] uppercase tracking-[0.28em] text-[var(--ft-gold)] font-semibold">Error 404</div>
        <h1 className="mt-3 text-[clamp(3rem,8vw,5.5rem)] font-bold leading-[1] tracking-tight text-[var(--ft-black)]">
          Page not found.
        </h1>
        <p className="mt-6 text-[var(--ft-muted)] max-w-md mx-auto">
          The page you&apos;re looking for has been moved, renamed, or never existed.
          Let&apos;s get you back to something useful.
        </p>
        <div className="mt-8 flex gap-3 justify-center flex-wrap">
          <Link to="/" className="ft-btn-primary" data-testid="notfound-home">
            <Home size={14} /> Return Home
          </Link>
          <Link to="/contact" className="ft-btn-secondary" data-testid="notfound-contact">
            Contact Us <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  </PublicLayout>
);

export const ServerError = () => (
  <PublicLayout>
    <section className="py-24 min-h-[70vh] bg-[var(--ft-soft-gray)]">
      <div className="ft-container max-w-2xl text-center">
        <AlertTriangle size={40} className="mx-auto mb-4 text-[var(--ft-gold)]" />
        <div className="text-[0.65rem] uppercase tracking-[0.28em] text-[var(--ft-gold)] font-semibold">Error 500</div>
        <h1 className="mt-3 text-[clamp(2.5rem,6vw,4rem)] font-bold leading-[1] tracking-tight text-[var(--ft-black)]">
          Something went wrong on our end.
        </h1>
        <p className="mt-6 text-[var(--ft-muted)] max-w-md mx-auto">
          Our team has been notified. Please refresh the page in a moment,
          or reach out and we&apos;ll help you right away.
        </p>
        <div className="mt-8 flex gap-3 justify-center flex-wrap">
          <Link to="/" className="ft-btn-primary" data-testid="500-home">
            <Home size={14} /> Return Home
          </Link>
          <Link to="/contact" className="ft-btn-secondary" data-testid="500-contact">
            Contact Support <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  </PublicLayout>
);

export const Cookies = () => (
  <PublicLayout>
    <section className="bg-white py-20 border-b border-[var(--ft-border)]">
      <div className="ft-container max-w-3xl">
        <div className="ft-eyebrow mb-5">Legal</div>
        <span className="ft-gold-rule-lg" />
        <h1 className="mt-6 text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.02] tracking-tight">Cookie Policy</h1>
        <div className="mt-6 text-sm text-[var(--ft-muted)]">
          Effective {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        </div>
      </div>
    </section>
    <section className="py-16">
      <div className="ft-container max-w-3xl text-[var(--ft-charcoal)] leading-relaxed space-y-8 text-[15px]">
        <div>
          <h2 className="text-xl font-semibold mb-2 text-[var(--ft-black)]">What are cookies?</h2>
          <span className="ft-gold-rule" />
          <p className="mt-4">Cookies are small text files placed on your device to remember information about your visit. FLOWTALIS™ uses cookies and similar local-storage keys to keep you signed in to the client portal, remember preferences, and measure aggregate usage of our platform.</p>
        </div>
        <div>
          <h2 className="text-xl font-semibold mb-2 text-[var(--ft-black)]">How we use cookies</h2>
          <span className="ft-gold-rule" />
          <ul className="mt-4 space-y-2 list-disc pl-5">
            <li><strong>Essential</strong> — session tokens for the client portal (`flowtalis_token`), and preferences such as the completion state of the onboarding tour.</li>
            <li><strong>Analytics</strong> — Google Analytics 4 uses cookies to help us understand which pages perform, which flows convert, and where friction exists. All analytics data is aggregated and anonymised.</li>
            <li><strong>Transactional email tracking</strong> — Resend, our email provider, appends tracking pixels to certain FLOWTALIS-sent emails so we know when an invoice was delivered or opened.</li>
          </ul>
        </div>
        <div>
          <h2 className="text-xl font-semibold mb-2 text-[var(--ft-black)]">Managing cookies</h2>
          <span className="ft-gold-rule" />
          <p className="mt-4">Most browsers let you refuse, block or delete cookies at any time. Doing so may prevent parts of the FLOWTALIS platform from working — for instance, you&apos;ll be signed out of the client portal.</p>
        </div>
        <div>
          <h2 className="text-xl font-semibold mb-2 text-[var(--ft-black)]">Contact</h2>
          <span className="ft-gold-rule" />
          <p className="mt-4">Questions about this policy? Email <a href="mailto:hello@flowtalis.com" className="text-[var(--ft-gold)] hover:underline">hello@flowtalis.com</a>.</p>
        </div>
      </div>
    </section>
  </PublicLayout>
);
